import React from 'react'
import {IWorkspaceItem} from "../../models/Workspace";
import {useGetWorkspaceQuery} from "../../store/Api";
import {usePutWorkspaceItemDetailsMutation} from "../../store/endpoints/WorkspaceItemsEndpoints";
import {TextField} from "@fluentui/react";
import {calculatePrice} from "../../logic/Product";
import {useEstimateProductBundlePriceCostMutation} from "../../store/endpoints/ProductBundleEndpoints";

interface PriceEntryProps {
    item: IWorkspaceItem;
    isExample?: boolean;
}

const PriceEntry: React.FC<PriceEntryProps> = (props) => {
    const workspace = useGetWorkspaceQuery(props.item.workspace, {
        skip: (!props.item.workspace || props.item.workspace.trim().length === 0 || props.item.workspace === "example workspace")
    });
    const [detailsChange] = usePutWorkspaceItemDetailsMutation();
    const [getPriceCostEstimate] = useEstimateProductBundlePriceCostMutation();
    const [currentVal, setCurrentVal] = React.useState<string | undefined>(undefined);
    // A bundle line has no price of its own to set: its price is whatever the items on it come to,
    // including any Override Price the bundle editor put on a line no vendor prices. Estimated the same
    // way the Cost cell estimates its cost, so the two cells describe the same configuration.
    const [bundlePrice, setBundlePrice] = React.useState<number | undefined>(undefined);

    React.useEffect(() => {
        if (props.item.details?.price != null && props.item.details?.price !== 0) {
            setCurrentVal(props.item.details.price.toString());
        }
    }, [props.item.details?.price])

    React.useEffect(() => {
        if (!props.item.isBundle || !props.item.sourceEntityId) {
            setBundlePrice(undefined);
            return;
        }
        const selection = (props.item.bundleSelections?.length ? props.item.bundleSelections
            : props.item.preparedConfiguration?.length ? props.item.preparedConfiguration
            : undefined);
        // The workspace's default markup has to go with the request, exactly as the product lines above apply
        // it through calculatePrice. Without it the server has no markup to work from unless the bundle carries
        // its own, and the bundle prices at cost. No markupType: a workspace markup is always a percentage
        // per the pricing hierarchy, and the server pins PERCENT alongside it. It outranks the bundle's own
        // default markup, which applies only where the workspace sets none.
        //
        // The storefront carries the two rungs below the bundle - its Product Settings markup and this
        // bundle's markup for it - which the server cannot reach without being told which storefront.
        getPriceCostEstimate({bundleId: props.item.sourceEntityId, request: {selection, markup: workspace?.data?.defaultMarkup,
            storefrontId: workspace?.data?.storefrontId, alwaysAvailable: props.item.details?.alwaysAvailable === true}})
            .unwrap()
            .then((res) => setBundlePrice(res.price))
            .catch(() => setBundlePrice(undefined));
    }, [props.item.isBundle, props.item.sourceEntityId, JSON.stringify(props.item.bundleSelections),
        JSON.stringify(props.item.preparedConfiguration), props.item.details?.alwaysAvailable,
        workspace?.data?.defaultMarkup, workspace?.data?.storefrontId, getPriceCostEstimate]);

    let calculatedPrice = props.item.product
        ? calculatePrice(props.item.product, props.item.details)
        : undefined;

    if((calculatedPrice === undefined || calculatedPrice === 0) && props.item.product) {
        calculatedPrice = calculatePrice(props.item.product, props.item.details, workspace?.data?.defaultMarkup, workspace?.data?.defaultListDiscount)
    }

    if (calculatedPrice === 0) calculatedPrice = undefined;

    // A zero estimate means nothing on the bundle carries a cost or an Override Price; "Not Set" says that
    // better than "$0.00", which reads as free. Two decimals to match the product lines beside it.
    if (props.item.isBundle) calculatedPrice = bundlePrice ? bundlePrice.toFixed(2) : undefined;

    const update = (v: string | undefined) => {
        if(props.isExample) return;
        detailsChange({
            workspace: props.item.workspace,
            product: props.item.id,
            details: {
                ...props.item.details,
                price: v ? parseFloat(v) : undefined,
            }
        })
    }

    return (<>
        <TextField
            placeholder={calculatedPrice?.toString() ?? 'Not Set'}
            value={currentVal}
            prefix={'$'}
            type={'number'}
            className={'italic-placeholder'}
            title={props.item.isBundle && calculatedPrice !== undefined ? 'Priced from the items on this bundle' : undefined}
            onBlur={(e) => update(e.target.value)}
            onKeyDown={(e) => {
                if (e.key === 'Enter') {
                    update(e.currentTarget.value);
                }
            }}
            onChange={(e, v) => {
                setCurrentVal(v);
            }}
            disabled={props.item.isBundle}
        />
    </>)
}

export default PriceEntry;
